import _ from 'lodash';

import { BusinessAccoladeFragment, useFetchBusinessAccoladesQuery } from 'generated/graphql';
import { ExpandableParagraph, Typography } from 'omakase';
import { consumer, withContext } from '@tock/coretypes';
import { css } from '@emotion/react';
import { gql } from '@apollo/client';
import Anchor from 'components/Anchor';
import pluralize from 'pluralize';

const DEFAULT_ICON_LINK = `https://storage.googleapis.com/tock-public-assets/accolades-images/Default.png`;

interface Props {
  business: consumer.IBusinessConfig;
}

const awardLinkSubtextContainer = css`
  text-decoration: underline;
`;

export const BUSINESS_ACCOLADES_FOR_CURRENT_BUSINESS = gql`
  query FetchBusinessAccolades @alertOwner(value: GUEST) {
    currentBusiness {
      businessAccoladesList {
        id
        ...BusinessAccolade
      }
    }
  }

  fragment BusinessAccolade on BusinessAccoladeOutput {
    accoladeType {
      accoladeTypeRating {
        id
        isHalfPoints
        maxValue
        minValue
        name
        systemType
      }
      displayName
      hasAchievement
      hasCritic
      hasLink
      iconImgLink
      id
      isActive
      name
      orgLink
    }
    achievement
    accoladeTypeName
    author
    archived
    id
    link
    ordering
    organization
    ratingNumber
    ratingString
    year
    yearDisplay
  }
`;

export const getRatingString = (accolade: BusinessAccoladeFragment) => {
  let rating;
  if (accolade.accoladeType?.accoladeTypeRating?.systemType) {
    const ratingSystem = accolade.accoladeType?.accoladeTypeRating?.systemType;

    if (ratingSystem) {
      switch (accolade.accoladeType?.accoladeTypeRating?.systemType) {
        case 'RANKING':
          rating = `#${accolade.ratingNumber}`;
          break;
        default:
          rating = pluralize(
            _.capitalize(`${accolade.accoladeType?.accoladeTypeRating.systemType}`),
            _.toNumber(accolade.ratingNumber),
            true
          );
      }
    }
  }

  return rating;
};

const About: React.FunctionComponent<Props> = ({ business }) => {
  const { data: businessAccoladeData } = useFetchBusinessAccoladesQuery();

  const renderNewAward = (accolade: BusinessAccoladeFragment): JSX.Element => {
    return accolade.link ? (
      <Anchor
        className="u-unstyledLink u-inlineBlock"
        target="_blank"
        href={accolade.link}
        title={`Visit accolade website`}
        data-testid="accolade-link"
      >
        {renderNewAwardInternal(accolade)}
      </Anchor>
    ) : (
      renderNewAwardInternal(accolade)
    );
  };

  const renderNewAwardInternal = (accolade: BusinessAccoladeFragment): JSX.Element => {
    return (
      <div className="ProfileAbout-awardsListItem">
        <div className="ProfileAbout-awardsImage">
          <img
            data-testid={'accolade-img-link'}
            src={accolade.accoladeType?.iconImgLink ?? DEFAULT_ICON_LINK}
            alt={accolade.link ? `${accolade.accoladeTypeName} logo` : `Award icon`}
          />
        </div>
        <div className="ProfileAbout-awardsContent">
          <Typography
            className="ProfileAbout-awardsLineTitle"
            component="h5"
            variant="body02Strong"
            data-testid={'accolade-achievement-rating'}
          >
            {_.join(_.filter([accolade.achievement, getRatingString(accolade)]), ', ')}
          </Typography>
          <Typography
            className="ProfileAbout-awardsLineSubTitle"
            css={accolade.link && awardLinkSubtextContainer}
            variant="body02"
            color={accolade.link ? undefined : 'fgSubtle'}
            data-testid={'accolade-name'}
          >
            {_.join(_.filter([accolade.accoladeTypeName, accolade.yearDisplay]), ', ')}
          </Typography>
        </div>
      </div>
    );
  };

  const sortedAccolades = _.orderBy(
    businessAccoladeData?.currentBusiness?.businessAccoladesList,
    'ordering'
  ).filter((businessAccolade) => businessAccolade.archived === false);
  if (
    !business.description &&
    !(
      businessAccoladeData &&
      (businessAccoladeData?.currentBusiness?.businessAccoladesList?.length ?? 0) > 0
    )
  ) {
    return null;
  }

  return (
    <div className="ProfileAbout">
      {business.description && (
        <ExpandableParagraph
          paragraphText={business.description}
          cutoffLength={500}
          linkify={true}
          data-testid="about-business-description"
        />
      )}
      {businessAccoladeData &&
        (businessAccoladeData?.currentBusiness?.businessAccoladesList?.length ?? 0) > 0 && (
          <div className="ProfileAbout-awards" data-testid="awards-section">
            <ul>
              {_.map(sortedAccolades, (accolade, idx) => (
                <li key={idx}>{renderNewAward(accolade)}</li>
              ))}
            </ul>
          </div>
        )}
    </div>
  );
};

export default withContext(About);
